import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {CheckCircle, ArrowRight, Mail, HelpCircle, Send} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { lumi } from '../lib/lumi';
import toast from 'react-hot-toast';

const PaymentSuccess: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [showHelp, setShowHelp] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);

  useEffect(() => {
    // Si l'utilisateur n'est pas connecté, le rediriger vers l'accueil après 5 secondes
    if (!isAuthenticated) {
      const timer = setTimeout(() => {
        navigate('/');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [isAuthenticated, navigate]);

  const handleAccessClick = () => {
    if (isAuthenticated) {
      navigate('/exercices');
    } else {
      navigate('/');
    }
  };

  const handleResendCode = async () => {
    if (!isAuthenticated || !user) {
      toast.error("Vous devez être connecté pour renvoyer le code");
      return;
    }

    setResendLoading(true);
    try {
      await lumi.functions.invoke('sendAccessCodeEmail', {
        method: 'POST'
      });

      // Track resend_code event (silent, non-blocking)
      try {
        await lumi.entities.user_events.create({
          userId: user.userId,
          eventType: 'resend_code',
          timestamp: new Date().toISOString(),
          metadata: {},
          creator: user.userId,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        });
      } catch (trackError) {
        console.error("Failed to track resend_code (non-critical):", trackError);
      }

      toast.success("✅ Code renvoyé ! Vérifiez vos emails (et vos spams).", { duration: 5000 });
    } catch (error: any) {
      console.error("Resend failed:", error);
      toast.error("❌ Erreur lors du renvoi. Contactez le support.", { duration: 5000 });
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="max-w-md w-full bg-gray-800 rounded-2xl p-8 text-center border-2 border-blue-500 shadow-2xl"
      >
        {/* Icône de succès */}
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
          className="mb-6"
        >
          <div className="w-20 h-20 bg-blue-500 rounded-full flex items-center justify-center mx-auto">
            <CheckCircle className="w-12 h-12 text-white" />
          </div>
        </motion.div>

        {/* Titre */}
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="text-3xl font-bold text-white mb-4"
        >
          Paiement confirmé
        </motion.h1>

        {/* Message principal */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="text-xl text-gray-300 mb-8"
        >
          Votre accès est prêt.
        </motion.p>

        {/* Message secondaire */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="text-sm text-gray-400 mb-8"
        >
          Un email contenant votre code d'accès vous a été envoyé.
          <br />
          <span className="text-blue-400">Pensez à vérifier vos spams.</span>
        </motion.p>

        {/* CTA principal */}
        <motion.button
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          onClick={handleAccessClick}
          className="w-full bg-gradient-to-r from-blue-600 to-blue-500 text-white py-4 rounded-lg font-bold text-lg hover:from-blue-700 hover:to-blue-600 transition-all shadow-lg hover:shadow-blue-500/25 flex items-center justify-center gap-2"
        >
          Accéder à mon espace
          <ArrowRight className="w-5 h-5" />
        </motion.button>

        {/* Information complémentaire */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="text-xs text-gray-500 mt-6"
        >
          Vous allez recevoir votre code d'accès par email dans quelques instants.
        </motion.p>

        {/* Bouton renvoyer le code */}
        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.9 }}
          onClick={handleResendCode}
          disabled={resendLoading}
          className="mt-4 text-blue-400 hover:text-blue-300 text-sm font-medium flex items-center justify-center gap-2 mx-auto disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Send className="w-4 h-4" />
          {resendLoading ? "Envoi en cours..." : "Je n'ai pas reçu mon code"}
        </motion.button>

        {/* Bouton aide */}
        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          onClick={() => setShowHelp(!showHelp)}
          className="mt-6 text-gray-400 hover:text-gray-300 text-xs flex items-center justify-center gap-1 mx-auto"
        >
          <HelpCircle className="w-4 h-4" />
          Que faire si je ne reçois pas mon code ?
        </motion.button>

        {/* Guide utilisateur (s'affiche au clic) */}
        {showHelp && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="mt-4 bg-gray-700/50 rounded-lg p-4 text-left border border-gray-600"
          >
            <h4 className="text-white font-bold mb-3 text-sm">📧 Problème de réception ?</h4>
            <ul className="text-gray-300 text-xs space-y-2">
              <li className="flex items-start gap-2">
                <span className="text-blue-400 font-bold">1.</span>
                <span><strong>Vérifiez vos spams/courrier indésirable</strong> - C'est souvent là que l'email arrive.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-400 font-bold">2.</span>
                <span><strong>Attendez 2-3 minutes</strong> - L'envoi peut prendre un peu de temps.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-400 font-bold">3.</span>
                <span><strong>Cliquez sur "Je n'ai pas reçu mon code"</strong> ci-dessus pour renvoyer l'email.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-400 font-bold">4.</span>
                <span><strong>Contactez le support</strong> : <a href="mailto:gregory64.mendez@gmail.com" className="text-blue-400 hover:underline">gregory64.mendez@gmail.com</a></span>
              </li>
            </ul>
            <p className="text-gray-400 text-xs mt-3 italic">
              💡 Astuce : Ajoutez "Body Titan Fitness" à vos contacts pour éviter les spams à l'avenir.
            </p>
          </motion.div>
        )}
      </motion.div>
    </div>
  );
};

export default PaymentSuccess;
